import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const FULL_WIDTH_PROP = 'fullWidth';
const WELCOME_PAGE_PROP = 'welcomePage';
const HAS_EMAIL = 'hasEmail';
const MAX_WIDTH = 'maxWidth';

interface MessageBoxProps {
  [FULL_WIDTH_PROP]: boolean;
  [WELCOME_PAGE_PROP]: boolean;
  [MAX_WIDTH]: string;
}

interface InfoBlockProps {
  [HAS_EMAIL]: boolean;
}

const messageBoxOptions = {
  shouldForwardProp: (propName: string) =>
    ![FULL_WIDTH_PROP, WELCOME_PAGE_PROP, MAX_WIDTH].includes(propName),
};

const infoBlockOptions = {
  shouldForwardProp: (propName: string) => propName !== HAS_EMAIL,
};

export const MessageBoxStyled = styled('div')`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 13px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

export const MessageBoxTitleStyled = styled(Typography)`
  ${({ theme }) => {
    const { breakpoints } = theme;

    const tabletMaxWidth = breakpoints.values.xl;
    const tableMinWidth = breakpoints.values.sm;

    return theme.breakpoints.between(tableMinWidth, tabletMaxWidth);
  }} {
    text-wrap: nowrap;
  }
`;

export const MainBlocksStyled = styled(
  Grid,
  messageBoxOptions,
)<MessageBoxProps>`
  border-radius: 20px;
  flex-wrap: nowrap;
  flex-direction: row;
  gap: 0;
  padding: 32px 48px;
  margin: 0;
  background-color: ${({ theme }) => theme.palette.background.default};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '870px')};
  max-width: ${({ maxWidth }) => maxWidth};

  ${({ theme }) => theme.breakpoints.down('xl')} {
    padding: 32px 48px 28px;
    gap: 2px;
    width: ${({ fullWidth }) => (fullWidth ? '100%' : '552px')};
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : '326px')};
    flex-direction: column;
    gap: 32px;
    padding: 42px 32px;
  }
`;

export const InfoBlockStyled = styled(Grid, infoBlockOptions)<InfoBlockProps>`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  gap: 32px;

  div:first-of-type {
    display: flex;
    align-items: center;

    img {
      height: 64px;
    }
  }

  div:last-of-type {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;
    text-align: left;
    gap: 8px;
    letter-spacing: 0.3px;
    line-height: 22px;

    p:first-of-type {
      font-size: 20px;
      line-height: 28px;
    }

    p:last-of-type {
      font-size: 16px;

      span {
        letter-spacing: 0.2px;

        .hasEmail {
          text-decoration: none;
        }
        a {
          color: inherit;
          text-decoration: ${({ hasEmail }) =>
            hasEmail ? 'underline' : 'none'};
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-direction: column;
    gap: 24px;

    div:first-of-type {
      img {
        height: 48px;
      }
    }

    div:last-of-type {
      gap: 4px;

      p:first-of-type {
        font-size: 18px;
        line-height: 26px;
      }

      p:last-of-type {
        line-height: 24px;
      }
    }
  }
`;

export const ButtonBlockStyled = styled(Grid)`
  justify-content: flex-end;
  align-items: center;
  display: flex;
  display: flex;

  button {
    background-color: transparent;
    color: inherit;
    letter-spacing: 0.5px;
    font-size: 16px;
  }

  ${({ theme }) => {
    const { breakpoints } = theme;

    const tabletMinWidth = breakpoints.values.md;
    const mobileMaxWidth = breakpoints.values.sm;

    return theme.breakpoints.between(mobileMaxWidth, tabletMinWidth);
  }} {
    text-wrap: nowrap;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: contents;

    button {
      font-size: 14px;
      line-height: 26px;
    }
  }
`;
